import { useEffect, useState } from 'react';
import { userAgent } from '@tink/tink-link-core/src/user-agent-detector';
import { ConfigurationModel } from '@tink/config/src/tinkLinkConfig';
import { CredentialsEntity, ThirdpartyAuthenticationPayload } from '../../models/credentials';
import { nestedPopup } from '../nested-popup';
import { applicationParameters } from '../application-parameters';
import { navigateUserToInitialTinkLinkAfterThirdPartyRedirect } from '../../models/configuration';
import { useConfiguration } from '../../components/ApplicationProviders/ConfigurationProvider/ConfigurationProvider';

export enum NavigationStrategy {
  POPUP,
  REDIRECT,
}

export type NavigationStrategyContext = {
  isIframe: boolean;
  hasAppUri: boolean;
  hasAutoRedirectMobile: boolean;
  isMobile: boolean;
  navigateUserToInitialTinkLinkFeatureFlag: boolean;
};

export const calculateNavigationStrategy = ({
  isIframe,
  hasAppUri,
  hasAutoRedirectMobile,
  isMobile,
  navigateUserToInitialTinkLinkFeatureFlag,
}: NavigationStrategyContext) => {
  const isEmbedded = hasAppUri || isIframe;
  const isEmbeddedShouldUseRedirect = hasAppUri && hasAutoRedirectMobile;

  if (isMobile && navigateUserToInitialTinkLinkFeatureFlag) {
    return NavigationStrategy.POPUP;
  }

  if (isEmbedded && !isEmbeddedShouldUseRedirect) {
    return NavigationStrategy.POPUP;
  }

  return NavigationStrategy.REDIRECT;
};

export interface ThirdpartyRedirectorInterface {
  url: string;
  navigate: () => void;
  shouldNavigateByPopup: () => boolean;
  shouldNavigateByRedirect: () => boolean;
}

export type ThirdpartyRedirectorParams = {
  supplementalInformation: string;
  configuration: ConfigurationModel;
  isIframe?: boolean;
  hasAppUri?: boolean;
  hasAutoRedirectMobile?: boolean;
};

export class ThirdpartyRedirector implements ThirdpartyRedirectorInterface {
  public url = '';

  private navigationStrategy: NavigationStrategy | undefined = undefined;

  public shouldNavigateByPopup() {
    return this.navigationStrategy === NavigationStrategy.POPUP;
  }

  public shouldNavigateByRedirect() {
    return this.navigationStrategy === NavigationStrategy.REDIRECT;
  }

  constructor({
    configuration,
    supplementalInformation,
    isIframe = Boolean(applicationParameters.get('isIframe')),
    hasAppUri = Boolean(applicationParameters.get('appUri')),
    hasAutoRedirectMobile = Boolean(applicationParameters.get('autoRedirectMobile')),
  }: ThirdpartyRedirectorParams) {
    const parsedSupplementalInformation: ThirdpartyAuthenticationPayload =
      JSON.parse(supplementalInformation);
    const { desktop, ios, android } = parsedSupplementalInformation;

    if (userAgent.isAppleDevice && ios && ios.deepLinkUrl) {
      this.url = ios.deepLinkUrl;
    }

    if (userAgent.isAndroidDevice && android && android.intent) {
      this.url = android.intent;
    }

    if (userAgent.isDesktop && desktop && desktop.url) {
      this.url = desktop.url;
    }

    // If we are in an iframe we fallback to a popup, since we can't redirect to the bank's website in an iframe due to
    // X-Frame-Options: sameorigin being set by the bank website. This does not apply for mobile units where we instead rely on
    // an A being present with the proper href to the universal link
    this.navigationStrategy = calculateNavigationStrategy({
      isIframe,
      hasAppUri,
      hasAutoRedirectMobile,
      isMobile: userAgent.isMobile,
      navigateUserToInitialTinkLinkFeatureFlag:
        navigateUserToInitialTinkLinkAfterThirdPartyRedirect(configuration),
    });
  }

  public navigate() {
    if (this.shouldNavigateByPopup()) {
      nestedPopup.open(this.url);
    } else {
      window.location.href = this.url;
    }
  }
}

export type UseThirdPartyRedirector = (credentials: CredentialsEntity) => {
  thirdPartyRedirector: ThirdpartyRedirectorInterface | undefined;
};

export const useThirdPartyRedirector: UseThirdPartyRedirector = (credentials) => {
  const { data: configuration } = useConfiguration();
  const [thirdPartyRedirector, setThirdPartyRedirector] = useState<
    ThirdpartyRedirector | undefined
  >(undefined);

  useEffect(() => {
    if (!credentials.supplementalInformation) {
      // There's an issue on aggregation that in some cases credentials turn to `AWAITING_THIRD_PARTY_APP_AUTHENTICATION` status,
      // but the `supplementalInformation` is not present yet and will be available seconds later
      return;
    }

    setThirdPartyRedirector(
      new ThirdpartyRedirector({
        configuration,
        supplementalInformation: credentials.supplementalInformation,
      })
    );
  }, [configuration, credentials.supplementalInformation]);

  return { thirdPartyRedirector };
};
